import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Map as MapIcon } from 'lucide-react';

const readTheme = () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light');

export function MapCard({ request }) {
    const { t, i18n } = useTranslation();
    const [info, setInfo] = useState(null);
    const [src, setSrc] = useState(null);
    const [theme, setTheme] = useState(readTheme);

    useEffect(() => {
        const obs = new MutationObserver(() => setTheme(readTheme()));
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const d = await (await request('/map/info')).json();
                if (alive) setInfo(d);
            } catch {
                if (alive) setInfo({ available: false });
            }
        };
        const first = setTimeout(load, 0);
        const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
        return () => { alive = false; clearTimeout(first); clearInterval(id); };
    }, [request]);

    useEffect(() => {
        if (!info?.available) return undefined;
        let alive = true;
        let url = null;
        (async () => {
            try {
                const res = await request(`/map?theme=${theme}`);
                if (!res.ok) throw new Error('map');
                url = URL.createObjectURL(await res.blob());
                if (alive) setSrc(url);
            } catch {
                if (alive) setSrc(null);
            }
        })();
        return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    }, [request, info?.available, info?.version, theme]);

    const when = info?.date && info?.time
        ? new Date(`${info.date}T${info.time}`).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
        : null;

    return (
        <section className="mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
                <MapIcon className="w-4 h-4" />{t('maison.mapTitle')}
                {when && <span className="normal-case font-normal tracking-normal">· {when}</span>}
            </h3>
            <div className="bg-card border border-border rounded-xl p-2 flex items-center justify-center min-h-32">
                {src
                    ? <img src={src} alt={t('maison.mapTitle')} className="w-full h-auto rounded-lg" />
                    : <span className="text-sm text-muted-foreground py-8">{t('maison.mapUnavailable')}</span>}
            </div>
        </section>
    );
}
